"use client";

import * as React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2, Sparkles, Building2, UtensilsCrossed, Bath } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
interface FormValues {
  name: string;
  phone: string;
  email: string;
  address: string;
  project_type: string;
  details: string;
  contact_method: string;
  timing: string;
  budget: string;
  consent: boolean;
}
const relatedServices = [{
  title: "Whole-Home",
  description: "Complete interior transformations, structural layouts, and comprehensive architectural renovations.",
  to: "/WholeHomeRoomRemodelingPage",
  icon: Building2
}, {
  title: "Kitchen Remodeling",
  description: "Custom cabinetry, chef-inspired layouts, quartz surfaces, and bespoke lighting fixtures.",
  to: "/KitchenRemodelingPage",
  icon: UtensilsCrossed
}, {
  title: "Bathroom Remodeling",
  description: "Master ensuites, frameless glass showers, freestanding soaking tubs, and luxury tiling.",
  to: "/BathroomRemodelingPage",
  icon: Bath
}];
const projectTypeOptions = ["Kitchen Remodeling", "Bathroom Remodeling", "Whole-Home Remodeling", "Home Additions", "Basement Remodeling", "Interior Remodeling", "Outdoor Living", "Deck Remodeling", "Other"];
const contactMethodOptions = ["Phone", "Text", "Email"];
const timingOptions = ["ASAP", "Within 1 month", "1-3 months", "3-6 months", "Just researching"];
const budgetOptions = ["Under $25,000", "$25,000-$50,000", "$50,000-$100,000", "$100,000+", "Not sure yet"];
export default function RelatedServicesEstimateForm() {
  const [formData, setFormData] = React.useState<FormValues>({
    name: "",
    phone: "",
    email: "",
    address: "",
    project_type: "",
    details: "",
    contact_method: "",
    timing: "",
    budget: "",
    consent: false
  });
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isSuccess, setIsSuccess] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage(null);
    if (!formData.consent) {
      setErrorMessage("Please accept the contact consent to proceed.");
      return;
    }
    if (!formData.name || !formData.phone || !formData.email || !formData.address || !formData.project_type || !formData.contact_method || !formData.timing) {
      setErrorMessage("Please complete all required fields.");
      return;
    }
    setIsLoading(true);
    try {
      const currentPath = typeof window !== "undefined" ? window.location.pathname : "";
      const searchParams = new URLSearchParams();
      searchParams.append("name", formData.name);
      searchParams.append("phone", formData.phone);
      searchParams.append("email", formData.email);
      searchParams.append("address", formData.address);
      searchParams.append("project_type", formData.project_type);
      searchParams.append("details", formData.details);
      searchParams.append("contact_method", formData.contact_method);
      searchParams.append("timing", formData.timing);
      searchParams.append("budget", formData.budget);
      searchParams.append("consent", formData.consent ? "true" : "false");

      // Hidden tracking fields
      searchParams.append("website", "DecaturHomeRenovation.com");
      searchParams.append("page", currentPath);
      searchParams.append("lead_source", "organic");
      const response = await fetch("https://hooks.zapier.com/hooks/catch/28361868/4mcz4d7/", {
        method: "POST",
        body: searchParams
      });
      if (response.ok) {
        setIsSuccess(true);
      } else {
        setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
      }
    } catch {
      setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
    } finally {
      setIsLoading(false);
    }
  };
  return <section id="relatedservicesestimateform" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header & Related Services */}
        <div className="mb-14 text-center max-w-3xl mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground">
            Explore Related Services
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default">
            Specialized renovation solutions tailored to elevate and preserve your home.
          </p>
        </div>

        {/* 3 Related Links Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {relatedServices.map((service, i) => {
          const IconComponent = service.icon;
          return <Card key={service.title} data-index={i} className="group relative border border-border/80 bg-card hover:border-foreground/30 transition-all duration-300 shadow-sm hover:shadow-md rounded-xl flex flex-col justify-between">
                <CardHeader>
                  <div className="size-11 rounded-lg bg-secondary/80 flex items-center justify-center text-foreground mb-3 group-hover:scale-105 transition-transform">
                    <IconComponent className="size-5 text-chart-1" />
                  </div>
                  <CardTitle className="font-serif text-xl sm:text-2xl font-semibold text-card-foreground">
                    {service.title}
                  </CardTitle>
                  <CardDescription className="font-default text-sm text-muted-foreground leading-relaxed mt-1">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <Button asChild variant="ghost" className="w-full justify-between px-3 py-2 text-foreground font-medium hover:bg-secondary/60 group-hover:text-chart-1">
                    <Link to={service.to}>
                      <span>Learn about {service.title}</span>
                      <ArrowRight className="size-4 ml-2 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>;
        })}
        </div>

        {/* Universal Estimate Form Card */}
        <div className="max-w-4xl mx-auto">
          <Card className="border border-border/90 bg-card/95 shadow-lg rounded-xl overflow-hidden backdrop-blur-xs">
            <CardHeader className="border-b border-border/70 pb-6 bg-secondary/30">
              <div className="flex items-center gap-2 text-chart-1 font-medium text-sm mb-1">
                <Sparkles className="size-4" />
                <span>Complimentary Project Consultation</span>
              </div>
              <CardTitle className="font-serif text-2xl sm:text-3xl font-semibold text-card-foreground">
                Request an Estimate
              </CardTitle>
              <CardDescription className="text-muted-foreground font-default text-sm sm:text-base">
                Tell us about your home and project vision. We will review your details and reach out promptly.
              </CardDescription>
            </CardHeader>

            <CardContent className="p-6 sm:p-8">
              {isSuccess ? <div className="py-12 px-4 text-center max-w-lg mx-auto animate-in fade-in zoom-in-95 duration-300">
                  <div className="size-16 rounded-full bg-secondary text-chart-1 flex items-center justify-center mx-auto mb-5 shadow-xs">
                    <CheckCircle2 className="size-9" />
                  </div>
                  <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-foreground mb-3">
                    Estimate Request Received
                  </h3>
                  <p className="text-muted-foreground font-default text-base leading-relaxed mb-8">
                    Thank you for reaching out. Our renovation team is reviewing your project details and will be in touch shortly through your preferred contact method.
                  </p>
                  <Button variant="outline" onClick={() => {
                setIsSuccess(false);
                setFormData({
                  name: "",
                  phone: "",
                  email: "",
                  address: "",
                  project_type: "",
                  details: "",
                  contact_method: "",
                  timing: "",
                  budget: "",
                  consent: false
                });
              }}>
                    Submit Another Request
                  </Button>
                </div> : <form onSubmit={handleSubmit} className="space-y-6">
                  {errorMessage && <Alert variant="destructive" className="border-destructive/40">
                      <AlertCircle className="size-4" />
                      <AlertTitle>Submission Error</AlertTitle>
                      <AlertDescription>{errorMessage}</AlertDescription>
                    </Alert>}

                  {/* Contact Information Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="form-name" className="font-default text-sm text-foreground">
                        Full Name <span className="text-destructive">*</span>
                      </Label>
                      <Input id="form-name" type="text" required placeholder="e.g. Jane Smith" value={formData.name} onChange={e => setFormData({
                    ...formData,
                    name: e.target.value
                  })} disabled={isLoading} className="bg-background" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="form-phone" className="font-default text-sm text-foreground">
                        Phone Number <span className="text-destructive">*</span>
                      </Label>
                      <Input id="form-phone" type="tel" required placeholder="Your phone number" value={formData.phone} onChange={e => setFormData({
                    ...formData,
                    phone: e.target.value
                  })} disabled={isLoading} className="bg-background" />
                    </div>
                  </div>

                  {/* Email and Address Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="form-email" className="font-default text-sm text-foreground">
                        Email Address <span className="text-destructive">*</span>
                      </Label>
                      <Input id="form-email" type="email" required placeholder="Your email address" value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} disabled={isLoading} className="bg-background" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="form-address" className="font-default text-sm text-foreground">
                        Street Address or ZIP Code <span className="text-destructive">*</span>
                      </Label>
                      <Input id="form-address" type="text" required placeholder="Decatur, GA or 30030" value={formData.address} onChange={e => setFormData({
                    ...formData,
                    address: e.target.value
                  })} disabled={isLoading} className="bg-background" />
                    </div>
                  </div>

                  {/* Project Type and Preferred Contact Method */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="form-project-type" className="font-default text-sm text-foreground">
                        Project Type <span className="text-destructive">*</span>
                      </Label>
                      <Select value={formData.project_type} onValueChange={val => setFormData({
                    ...formData,
                    project_type: val
                  })} disabled={isLoading}>
                        <SelectTrigger id="form-project-type" className="w-full bg-background">
                          <SelectValue placeholder="Select primary service" />
                        </SelectTrigger>
                        <SelectContent>
                          {projectTypeOptions.map(opt => <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="form-contact-method" className="font-default text-sm text-foreground">
                        Preferred Contact Method <span className="text-destructive">*</span>
                      </Label>
                      <Select value={formData.contact_method} onValueChange={val => setFormData({
                    ...formData,
                    contact_method: val
                  })} disabled={isLoading}>
                        <SelectTrigger id="form-contact-method" className="w-full bg-background">
                          <SelectValue placeholder="Select contact preference" />
                        </SelectTrigger>
                        <SelectContent>
                          {contactMethodOptions.map(opt => <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Timing and Budget */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="form-timing" className="font-default text-sm text-foreground">
                        Project Timing <span className="text-destructive">*</span>
                      </Label>
                      <Select value={formData.timing} onValueChange={val => setFormData({
                    ...formData,
                    timing: val
                  })} disabled={isLoading}>
                        <SelectTrigger id="form-timing" className="w-full bg-background">
                          <SelectValue placeholder="When would you like to start?" />
                        </SelectTrigger>
                        <SelectContent>
                          {timingOptions.map(opt => <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="form-budget" className="font-default text-sm text-foreground">
                        Estimated Budget
                      </Label>
                      <Select value={formData.budget} onValueChange={val => setFormData({
                    ...formData,
                    budget: val
                  })} disabled={isLoading}>
                        <SelectTrigger id="form-budget" className="w-full bg-background">
                          <SelectValue placeholder="Select an investment range" />
                        </SelectTrigger>
                        <SelectContent>
                          {budgetOptions.map(opt => <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Project Details Textarea */}
                  <div className="space-y-2">
                    <Label htmlFor="form-details" className="font-default text-sm text-foreground">
                      Project Details
                    </Label>
                    <Textarea id="form-details" rows={4} placeholder="Share your goals, architectural preferences, current space challenges, or specific requirements..." value={formData.details} onChange={e => setFormData({
                  ...formData,
                  details: e.target.value
                })} disabled={isLoading} className="bg-background min-h-[110px]" />
                  </div>

                  {/* Consent Checkbox */}
                  <div className="pt-2">
                    <div className="flex items-start gap-3">
                      <Checkbox id="form-consent" checked={formData.consent} onCheckedChange={checked => setFormData({
                    ...formData,
                    consent: checked === true
                  })} disabled={isLoading} className="mt-0.5" />
                      <Label htmlFor="form-consent" className="font-default text-sm text-muted-foreground leading-snug cursor-pointer font-normal">
                        I agree to be contacted about my project by phone, text, or email.{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-4 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-xs text-muted-foreground order-2 sm:order-1 text-center sm:text-left">
                      Need immediate assistance? Call{" "}
                      <a href="tel:4708479076" className="font-medium text-foreground underline underline-offset-2 hover:text-chart-1">
                        (470) 847-9076
                      </a>
                    </p>

                    <Button type="submit" size="lg" disabled={isLoading} className="w-full sm:w-auto px-8 bg-primary text-primary-foreground font-medium order-1 sm:order-2">
                      {isLoading ? <>
                          <Loader2 className="size-4 animate-spin mr-2" />
                          Sending Request...
                        </> : <>
                          Submit Estimate Request
                          <ArrowRight className="size-4 ml-2" />
                        </>}
                    </Button>
                  </div>
                </form>}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}